
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';

import { formatCurrency } from '../../../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import { setSalesData, setYears } from '../../../../../store/SalesDataReducer';
import type { RootState } from '../../../../../store/store';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import tailwindConfig from '../../../../../utils/tailwindColors';
import Badge from '../../../../common/Badge';
import { showToast } from '../../../../common/Notification';
import Table from '../../../../common/Table';

interface SalesData {
  id: string; // needed to respect React tr unique key
  'INFORMATIONS SUR VOTRE PARC': string | JSX.Element;
  [year: string]: string | JSX.Element;
}

function CoproSold() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [data, setData] = useState<Record<string, Record<string, number | string>>>({});
  const [formattedData, setFormattedData] = useState<SalesData[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [error, setError] = useState<string | null>(null);
  const [formattedYears, setFormattedYears] = useState<string[]>([]);
  const dispatch = useDispatch();

  const generateYearData = (unformattedData: Record<string, Record<string, number | string>>, key: string, formatter: (value: number | string, year: string) => string | JSX.Element = (value) => value.toString()) => {
    const yearsUnformatted = Object.keys(unformattedData).sort((a, b) => Number(a) - Number(b));
    const latestYear = yearsUnformatted[yearsUnformatted.length - 1];

    return yearsUnformatted.reduce((acc, year) => {
      const formattedYear = year === latestYear ? `${latestYear} (en cours d'enregistrement)` : year;
      acc[formattedYear] = formatter(unformattedData[year][key], year);
      return acc;
    }, {} as Record<string, string | JSX.Element>);
  };

  const formatYears = (unformattedData: Record<string, Record<string, number | string>>): string[] => {
    if (!unformattedData || Object.keys(unformattedData).length === 0) {return [];}
    const extractedYears = Object.keys(unformattedData).sort((a, b) => Number(a) - Number(b));
    return extractedYears.map((year) => (year === extractedYears[extractedYears.length - 1] ? `${extractedYears[extractedYears.length - 1]} (en cours d'enregistrement)` : year));
  };

  const formatParkInfo = (unformattedData: Record<string, Record<string, number | string>>): SalesData[] => {
    if (!unformattedData || Object.keys(unformattedData).length === 0) {return [];}

    return [
      {
        id: '1',
        'INFORMATIONS SUR VOTRE PARC': 'Nb de ventes dans vos copros',
        ...generateYearData(unformattedData, 'nbSales'),
      },
      {
        id: '2',
        'INFORMATIONS SUR VOTRE PARC': <span className="text-red-500">Nb de DPE établis dans vos copros</span>,
        ...generateYearData(unformattedData, 'nbDpe'),
      },
      {
        id: '3',
        'INFORMATIONS SUR VOTRE PARC': 'Prix moyen des ventes dans vos copros',
        ...generateYearData(unformattedData, 'salesAveragePrice', (value) => `${formatCurrency(value)}`),
      },
      {
        id: '4',
        'INFORMATIONS SUR VOTRE PARC': 'Total des ventes sur vos copros',
        ...generateYearData(unformattedData, 'salesAmount', (value) => `${formatCurrency(value)}`),
      },
      {
        id: '5',
        'INFORMATIONS SUR VOTRE PARC': <span className="font-semibold">
          Commission à{' '}
          {company.commission}
          %
        </span>,
        ...generateYearData(unformattedData, 'salesCommission', (value) => <b>{formatCurrency(value)}</b>),
      },
    ];
  };

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    setStatus('PENDING');

    // Fetch sales data
    fetch(`/api/companies/${company.id}/stats/sales`, JWTHeader(jwt))
      .then((response) => {
        if (!response.ok) {
          throw new Error('Internal server error');
        }
        return response.json();
      })
      .then((result) => {
        setData(result);
        const formattedYearsResult = formatYears(result);
        setFormattedYears(formattedYearsResult);
        dispatch(setYears(formattedYearsResult));
        dispatch(setSalesData(result));
        setStatus('SUCCESS');
      })
      .catch((response_error) => {
        console.error('Error fetching sales data :', response_error);
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
        setError('Une erreur est survenue lors du chargement des données.');
        setStatus('ERROR');
      });
  }, [company, jwt, dispatch]);

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {return;}
    setFormattedData(formatParkInfo(data));
  }, [data, company.commission]);

  const parkInfoHeaders = ['INFORMATIONS SUR VOTRE PARC', ...formattedYears];

  return (
    <div>
      {status === 'PENDING' && (
        <div className="w-full text-center">
          <Table data={[]} headers={['INFORMATIONS SUR VOTRE PARC', '. . . .', '. . . .', '. . . .', '. . . .']} tableClass="w-full" />
          <Skeleton count={5} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-1" />
        </div>
      )}
      {status === 'ERROR'
        && (
          <Badge
            mode="error"
            text={error}
          />
        )}
      {status === 'SUCCESS' && formattedData
        && (
          <Table autoresize={false} data={formattedData} headers={parkInfoHeaders} tableClass="w-full min-h-56" />
        )}
    </div>
  );
}

export default CoproSold;
